import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { Plus, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const HOURS = Array.from({ length: 12 }, (_, i) => 9 + i); // 9am–8pm
const statusChip = {
  scheduled: "bg-white text-[#52525B] border-[#E5E5E2]",
  checked_in: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  in_chair: "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]",
  completed: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
  no_show: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  cancelled: "bg-[#F4F4F5] text-[#71717A] border-[#E5E5E2] line-through",
};

const dayStart = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const iso = (d) => d.toISOString();
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };
const same = (a, b) => a.toDateString() === b.toDateString();

export default function Scheduler() {
  const [date, setDate] = useState(dayStart(new Date()));
  const [view, setView] = useState("day");
  const [appts, setAppts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", start: "", duration: 30, reason: "" });

  const range = useMemo(() => {
    if (view === "day") return { start: dayStart(date), end: addDays(dayStart(date), 1) };
    const s = dayStart(date); const dow = s.getDay();
    const monday = addDays(s, -((dow + 6) % 7));
    return { start: monday, end: addDays(monday, 7) };
  }, [date, view]);

  const load = async () => {
    const [a, p] = await Promise.all([
      api.get("/appointments", { params: { start: iso(range.start), end: iso(range.end) } }),
      api.get("/patients"),
    ]);
    setAppts(a.data); setPatients(p.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range.start.getTime(), range.end.getTime()]);

  // Auto-sync Google Calendar every 30s while scheduler is open
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const { data } = await api.post("/oauth/google/sync-pull");
        const changed = data.imported + data.updated + data.removed;
        if (!cancelled && changed > 0) {
          const parts = [];
          if (data.imported) parts.push(`${data.imported} new`);
          if (data.updated) parts.push(`${data.updated} updated`);
          if (data.removed) parts.push(`${data.removed} removed`);
          toast.info(`Google Calendar: ${parts.join(" · ")}`);
          load();
        }
      } catch { /* silent if not connected */ }
    };
    run();
    const id = setInterval(run, 30000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line
  }, []);

  const openNew = (prefillDate) => {
    const d = prefillDate ? new Date(prefillDate) : new Date(date);
    if (!prefillDate) d.setHours(10, 0, 0, 0);
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setForm({ patient_id: patients[0]?.id || "", start: local, duration: 30, reason: "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id) return toast.error("Pick a patient");
    const start = new Date(form.start);
    const end = new Date(start.getTime() + form.duration * 60000);
    try {
      await api.post("/appointments", {
        patient_id: form.patient_id,
        start: start.toISOString(),
        end: end.toISOString(),
        reason: form.reason,
        status: "scheduled",
      });
      toast.success("Appointment scheduled");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/appointments/${id}/status`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const label = "text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-1 block";
  const inp = "w-full bg-white border border-[#E5E5E2] text-sm px-3 py-2 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#0F4C3A]";

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#E27B58] font-semibold mb-1">Chairside</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Scheduler</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border border-[#E5E5E2] rounded-md p-1 bg-white">
            <button data-testid="view-day" onClick={() => setView("day")} className={`text-xs px-3 py-1 rounded-sm ${view === "day" ? "bg-[#0F4C3A] text-white" : "text-[#52525B]"}`}>Day</button>
            <button data-testid="view-week" onClick={() => setView("week")} className={`text-xs px-3 py-1 rounded-sm ${view === "week" ? "bg-[#0F4C3A] text-white" : "text-[#52525B]"}`}>Week</button>
          </div>
          <button data-testid="new-appt-btn" onClick={() => openNew()} className="inline-flex items-center gap-2 bg-[#0F4C3A] text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-[#16654F]">
            <Plus className="w-4 h-4" /> New appointment
          </button>
          <button data-testid="sync-google-btn" onClick={async () => {
            try {
              const { data } = await api.post("/oauth/google/sync-pull");
              toast.success(`Fetched ${data.fetched} · ${data.imported} new · ${data.updated} updated · ${data.removed} removed`);
              load();
            } catch (e) { toast.error(formatApiError(e)); }
          }} className="inline-flex items-center gap-2 border border-[#E5E5E2] bg-white text-[#1C1C1C] text-sm font-medium px-4 py-2 rounded-md hover:bg-[#F4F4F5]">
            <RefreshCw className="w-4 h-4" /> Sync Google
          </button>
        </div>
      </div>

      {/* Date nav */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => setDate((d) => addDays(d, view === "day" ? -1 : -7))} className="p-1.5 border border-[#E5E5E2] bg-white rounded-sm hover:bg-[#F4F4F5]"><ChevronLeft className="w-4 h-4" /></button>
        <div className="font-display font-semibold text-lg tracking-tight">
          {view === "day"
            ? date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
            : `${range.start.toLocaleDateString("en-IN", { day: "numeric", month: "short" })} — ${addDays(range.end, -1).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`}
        </div>
        <button onClick={() => setDate((d) => addDays(d, view === "day" ? 1 : 7))} className="p-1.5 border border-[#E5E5E2] bg-white rounded-sm hover:bg-[#F4F4F5]"><ChevronRight className="w-4 h-4" /></button>
        <button onClick={() => setDate(dayStart(new Date()))} className="text-xs px-3 py-1.5 border border-[#E5E5E2] bg-white rounded-sm hover:bg-[#F4F4F5]">Today</button>
      </div>

      {view === "day" && (
        <div className="bg-white border border-[#E5E5E2] rounded-md overflow-hidden">
          {HOURS.map((h) => {
            const slot = new Date(date); slot.setHours(h, 0, 0, 0);
            const items = appts.filter((a) => new Date(a.start).getHours() === h && same(new Date(a.start), date));
            return (
              <div key={h} className="grid grid-cols-[80px_1fr] border-b border-[#E5E5E2] last:border-b-0 min-h-[68px]">
                <div className="p-3 text-xs font-mono-num text-[#52525B] border-r border-[#E5E5E2] bg-[#FDFDFB]">
                  {String(h).padStart(2, "0")}:00
                </div>
                <div className="p-2 space-y-1 cursor-pointer hover:bg-[#FDFDFB]" onClick={() => openNew(slot)}>
                  {items.length === 0 && <div className="text-xs text-[#A1A1AA] italic">Click to add…</div>}
                  {items.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 px-3 py-2 border border-[#E5E5E2] rounded-sm bg-white" data-testid={`appt-${a.id}`}>
                      <div className="text-xs font-mono-num text-[#52525B] w-16">
                        {new Date(a.start).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{a.patient_name} <span className="text-xs text-[#52525B] font-mono-num ml-2">{a.patient_mrn}</span></div>
                        <div className="text-xs text-[#52525B] truncate">{a.reason || "Consultation"}</div>
                      </div>
                      <select
                        value={a.status}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setStatus(a.id, e.target.value)}
                        className={`text-[10px] uppercase tracking-wider font-semibold border px-2 py-1 rounded-sm ${statusChip[a.status]}`}
                      >
                        {["scheduled", "checked_in", "in_chair", "completed", "no_show", "cancelled"].map((s) => (
                          <option key={s} value={s}>{s.replace("_"," ")}</option>
                        ))}
                      </select>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await api.post("/whatsapp/send-reminder", { appointment_id: a.id });
                            toast.success("WhatsApp reminder sent");
                          } catch (err) { toast.error(formatApiError(err)); }
                        }}
                        title="Send WhatsApp reminder"
                        data-testid={`wa-remind-${a.id}`}
                        className="text-xs px-2 py-1 border border-[#E5E5E2] rounded-sm hover:bg-[#F0FDF4] text-[#0F4C3A]"
                      >WA</button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "week" && (
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 7 }, (_, i) => addDays(range.start, i)).map((d) => {
            const items = appts.filter((a) => same(new Date(a.start), d));
            return (
              <div key={d.toISOString()} className="bg-white border border-[#E5E5E2] rounded-md">
                <div className={`px-3 py-2 border-b border-[#E5E5E2] ${same(d, new Date()) ? "bg-[#F0FDF4]" : "bg-[#FDFDFB]"}`}>
                  <div className="text-[10px] uppercase tracking-widest text-[#52525B]">{d.toLocaleDateString("en-IN", { weekday: "short" })}</div>
                  <div className="font-display font-bold text-lg">{d.getDate()}</div>
                </div>
                <div className="p-2 space-y-1 min-h-[240px]">
                  {items.length === 0 && <div className="text-[11px] text-[#A1A1AA] italic p-2">No appts</div>}
                  {items.map((a) => (
                    <div key={a.id} className={`text-[11px] p-2 rounded-sm border ${statusChip[a.status]}`}>
                      <div className="font-mono-num">{new Date(a.start).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
                      <div className="font-semibold truncate">{a.patient_name}</div>
                      <div className="truncate">{a.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="new-appt-modal">
          <form onSubmit={submit} className="w-full max-w-md bg-white rounded-md shadow-2xl">
            <div className="px-6 py-4 border-b border-[#E5E5E2] flex justify-between items-center">
              <h3 className="font-display text-lg font-bold tracking-tight">New appointment</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-sm text-[#52525B]">Close</button>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className={label}>Patient *</label>
                <select data-testid="appt-patient" required className={inp} value={form.patient_id} onChange={(e) => setForm((f) => ({ ...f, patient_id: e.target.value }))}>
                  <option value="">Select…</option>
                  {patients.map((p) => (
                    <option key={p.id} value={p.id}>{p.full_name} · {p.mrn}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Starts</label>
                  <input data-testid="appt-start" type="datetime-local" required className={inp} value={form.start} onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))} />
                </div>
                <div>
                  <label className={label}>Duration (min)</label>
                  <input type="number" min="10" step="5" className={inp} value={form.duration} onChange={(e) => setForm((f) => ({ ...f, duration: parseInt(e.target.value || 30) }))} />
                </div>
              </div>
              <div>
                <label className={label}>Reason</label>
                <input data-testid="appt-reason" className={inp} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Root canal, Follow-up" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E5E5E2] flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="text-sm px-4 py-2 border border-[#E5E5E2] rounded-md">Cancel</button>
              <button type="submit" data-testid="save-appt-btn" className="text-sm px-4 py-2 bg-[#0F4C3A] text-white rounded-md hover:bg-[#16654F]">Schedule</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
